import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NAVIGATION_ITEMS, COMPANY_INFO, HERO_CONTENT } from '../../constants';
import { cn } from '../../lib/utils';
import { handlePageNavigation } from '../../utils/navigation';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check for interview overlays with high z-index that indicate an active interview
    const checkInterviewOverlay = () => {
      const interviewOverlays = document.querySelectorAll('.fixed.inset-0');
      const hasActiveInterview = Array.from(interviewOverlays).some(overlay => {
        const styles = window.getComputedStyle(overlay);
        const zIndex = parseInt(styles.zIndex) || 0;
        return zIndex >= 40 && overlay.querySelector('[class*="interview"], [class*="video"], [class*="ai"]');
      });
      setIsInterviewActive(hasActiveInterview);
    };

    // Initial check
    checkInterviewOverlay();

    // Set up observer to watch for DOM changes
    const observer = new MutationObserver(checkInterviewOverlay);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true, 
      attributes: true, 
      attributeFilter: ['class', 'style'] 
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDropdownToggle = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  // Handle navigation with auth clearing for public pages
  const handleNavClick = (href: string) => {
    // Don't clear auth if this is a post-login navigation (indicated by location state)
    const isPostLoginNavigation = location.state?.checkStatusOnly;

    // Clear auth when navigating to any main website section
    // This keeps the jobs app separate from the main website
    // Check both isAuthenticated state and localStorage for auth token
    const hasAuthToken = localStorage.getItem('authToken');
    const isMainWebsiteNavigation =
      href === '/' ||
      href === '/about' ||
      href.startsWith('/services/') ||
      href === '/services' ||
      href === '/contact' ||
      href === '/careers' ||
      href.startsWith('/careers/');

    if ((isAuthenticated || hasAuthToken) && isMainWebsiteNavigation && !isPostLoginNavigation) {
      logout();
      // Also clear localStorage directly to ensure it's cleared
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
    handlePageNavigation();
  };

  return (
    <header className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled 
        ? 'shadow-lg' 
        : '',
      isInterviewActive 
        ? 'opacity-0 pointer-events-none -translate-y-full' 
        : 'opacity-100 pointer-events-auto translate-y-0'
    )}>
      {/* Blurred Background Layer - only this gets blurred */}
      {isScrolled && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md"></div>
      )}
      
      {/* Background image - only visible when scrolled */}
      {isScrolled && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30 z-0"
          style={{
            backgroundImage: `url('${HERO_CONTENT.backgroundImage}')`,
            backgroundSize: '200% auto'
          }}
        ></div>
      )}

      {/* Main Navigation - positioned above the blurred background */}
      <nav className="container relative z-10">
        <div className="flex items-center justify-between py-6 relative">
          {/* Mobile Menu Button - Now on the left */}
          <button
            onClick={toggleMenu}
            className={cn(
              'lg:hidden p-2 rounded-lg transition-colors duration-200 mr-3',
              isScrolled 
                ? 'text-secondary-700 hover:bg-secondary-100' 
                : 'text-white hover:bg-cream-700/10'
            )}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" onClick={handlePageNavigation}>
            <img 
              src="/assets/images/branding/M-Telecommunication_logo_FA_3.svg" 
              alt={COMPANY_INFO.fullName}
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
            {NAVIGATION_ITEMS.map((item) => (
              <div key={item.label} className="relative group">
                {item.hasDropdown ? (
                  <div
                    className="flex items-center space-x-1 cursor-pointer"
                    onMouseEnter={() => setActiveDropdown(item.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    <Link
                      to={item.href}
                      className={cn(
                        'font-medium transition-colors duration-200 hover:text-primary-600',
                        location.pathname === item.href 
                          ? 'text-primary-600' 
                          : isScrolled ? 'text-secondary-300' : 'text-white'
                      )}
                      onClick={() => handleNavClick(item.href)}
                    >
                      {item.label}
                    </Link>
                    <ChevronDown 
                      className={cn(
                        'w-4 h-4 transition-transform duration-200',
                        activeDropdown === item.label ? 'rotate-180' : '',
                        isScrolled ? 'text-secondary-300' : 'text-white'
                      )} 
                    />
                    
                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {activeDropdown === item.label && item.dropdownItems && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 w-64 bg-secondary-800 rounded-lg shadow-xl border border-secondary-700 py-2 z-50"
                        >
                          {item.dropdownItems.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.label}
                              to={dropdownItem.href}
                              className="block px-4 py-2 text-secondary-400 hover:bg-secondary-700 hover:text-primary-600 transition-colors duration-200"
                              onClick={() => handleNavClick(dropdownItem.href)}
                            >
                              {dropdownItem.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'font-medium transition-colors duration-200 hover:text-primary-600',
                      location.pathname === item.href 
                        ? 'text-primary-600' 
                        : isScrolled ? 'text-secondary-300' : 'text-white'
                    )}
                    onClick={() => handleNavClick(item.href)}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button - Now also visible on mobile as a smaller button */}
          <div className="lg:block">
            <Link
              to="/contact"
              className="btn-futuristic text-sm lg:text-base px-3 lg:px-6 py-2 lg:py-3"
              onClick={handlePageNavigation}
            >
              <span className="hidden sm:inline">Contact Us</span>
              <span className="sm:hidden">Contact</span>
            </Link>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-secondary-200 shadow-lg"
            >
              <div className="py-4 space-y-2">
                {NAVIGATION_ITEMS.map((item) => (
                  <div key={item.label}>
                    {item.hasDropdown ? (
                      <div>
                        <button
                          onClick={() => handleDropdownToggle(item.label)}
                          className="flex items-center justify-between w-full px-4 py-2 text-left text-secondary-700 hover:bg-secondary-50 transition-colors duration-200"
                        >
                          {item.label}
                          <ChevronDown 
                            className={cn(
                              'w-4 h-4 transition-transform duration-200',
                              activeDropdown === item.label ? 'rotate-180' : ''
                            )} 
                          />
                        </button>
                        <AnimatePresence>
                          {activeDropdown === item.label && item.dropdownItems && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="bg-secondary-50 border-l-4 border-primary-500"
                            >
                              {item.dropdownItems.map((dropdownItem) => (
                                <Link
                                  key={dropdownItem.label}
                                  to={dropdownItem.href}
                                  className="block px-8 py-2 text-secondary-600 hover:text-primary-600 transition-colors duration-200"
                                  onClick={() => handleNavClick(dropdownItem.href)}
                                >
                                  {dropdownItem.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={cn(
                          'block px-4 py-2 transition-colors duration-200',
                          location.pathname === item.href 
                            ? 'text-primary-600 bg-primary-50' 
                            : 'text-secondary-700 hover:bg-secondary-50'
                        )}
                        onClick={() => handleNavClick(item.href)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="px-4 pt-4 border-t border-secondary-200">
                  <Link
                    to="/contact"
                    className="btn-futuristic w-full text-center"
                    onClick={handlePageNavigation}
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
